/**
 * Display-time cleanup for SEO discovery prose (defense in depth vs stored leaks).
 */

const stripMarkdownEmphasis = value => String(value || '').replace(/\*\*([^*]+)\*\*/g, '$1');

/** Convert ATX headings to paragraph breaks; never show raw ### in the UI. */
const stripMarkdownHeadings = value =>
  String(value || '')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/[ \t]*#{1,6}\s+/g, '\n\n');

/** Remove internal evidence ledger IDs that must never appear in reader-facing copy. */
const stripEvidenceIdsFromProse = value => {
  let out = String(value || '');
  out = out.replace(/\[\s*ev-[a-z0-9:_-]+(?:\s*,\s*ev-[a-z0-9:_-]+)*\s*\]/gi, '');
  out = out.replace(/\bev-[a-z0-9]+(?:[:_-][a-z0-9]+)*/gi, '');
  out = out.replace(/\[\s*:?[a-z]{2}(?::[a-z0-9_-]+)+\s*\]/gi, '');
  out = out.replace(/(^|[\s([{])(:[a-z]{2}(?::[a-z0-9_-]+)+)/gi, '$1');
  // Collapse horizontal runs only — keep paragraph breaks for display.
  out = out.replace(/[^\S\n]{2,}/g, ' ');
  out = out.replace(/\n{3,}/g, '\n\n');
  out = out.replace(/[^\S\n]+([.,;:!?])/g, '$1');
  return out.trim();
};

const sanitizeDisplayCopy = value => {
  let out = stripEvidenceIdsFromProse(stripMarkdownHeadings(stripMarkdownEmphasis(value)));
  // Display-time sentence capitalization (covers older stored copy with ". what")
  out = out.replace(/(^|[.!?]\s+)([a-z])/g, (_, lead, ch) => `${lead}${ch.toUpperCase()}`);
  return out;
};

/**
 * Split body text into readable paragraphs.
 * Prefers explicit \\n\\n breaks; soft-wraps long single blobs into ~2–3 sentence groups.
 */
const paragraphsFromText = value => {
  const cleaned = sanitizeDisplayCopy(value);
  if (!cleaned) return [];
  const byBreak = cleaned
    .split(/\n{2,}/)
    .map(p => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  if (byBreak.length > 1) return byBreak;

  const single = byBreak[0] || '';
  if (single.length < 360) return single ? [single] : [];

  const sentences = single.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [single];
  const paras = [];
  let buf = [];
  sentences.forEach(s => {
    const piece = s.trim();
    if (!piece) return;
    buf.push(piece);
    const words = buf.join(' ').split(/\s+/).length;
    if (buf.length >= 2 && words >= 45) {
      paras.push(buf.join(' '));
      buf = [];
    }
  });
  if (buf.length) paras.push(buf.join(' '));
  return paras.filter(Boolean);
};

export {
  stripMarkdownEmphasis,
  stripMarkdownHeadings,
  stripEvidenceIdsFromProse,
  sanitizeDisplayCopy,
  paragraphsFromText,
};
